import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import { useAuthStore } from './stores/auth.store';
import { Role } from './hooks/useAuth';
import { getFirstAllowedRoute } from '@zhibao/shared';
import AdminLayout from './layouts/AdminLayout';
import LoginPage from './pages/login/LoginPage';

/** ★ P1#6: 10 pages → React.lazy() + Suspense 懒加载 */
const DashboardPage = React.lazy(() => import('./pages/dashboard/DashboardPage'));
const ChannelsPage = React.lazy(() => import('./pages/channels/ChannelsPage'));
const OrdersPage = React.lazy(() => import('./pages/orders/OrdersPage'));
const OrderDetailPage = React.lazy(() => import('./pages/orders/OrderDetailPage'));
const ReportsPage = React.lazy(() => import('./pages/reports/ReportsPage'));
const CryptoToolPage = React.lazy(() => import('./pages/crypto-tool/CryptoToolPage'));
const ApiDocsPage = React.lazy(() => import('./pages/api-docs/ApiDocsPage'));
const ProfilePage = React.lazy(() => import('./pages/profile/ProfilePage'));
const TrackingPage = React.lazy(() => import('./pages/tracking/TrackingPage'));
const PoliciesPage = React.lazy(() => import('./pages/policies/PoliciesPage'));
const QuarantinePage = React.lazy(() => import('./pages/quarantine/QuarantinePage'));

/** 懒加载 Fallback */
const PageLoading: React.FC = () => (
  <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
);

/** 根路径重定向：按角色跳转到首个允许页面 */
const IndexRedirect: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const userRole = (user?.role as Role) || Role.DOWNSTREAM;
  return <Navigate to={getFirstAllowedRoute(userRole)} replace />;
};

/** ★ P1#7: ProtectedRoute 加 role 属性校验 — 路由级 RBAC 守卫 */
const ProtectedRoute: React.FC<{ children: React.ReactNode; roles?: Role[] }> = ({ children, roles }) => {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  if (!token) return <Navigate to="/login" replace />;

  if (roles && roles.length > 0) {
    const userRole = (user?.role as Role) || Role.DOWNSTREAM;
    if (!roles.includes(userRole)) {
      return <Navigate to={getFirstAllowedRoute(userRole)} replace />;
    }
  }

  return <>{children}</>;
};

/** Route 对应的角色列表 — 与 AdminLayout menuItems 保持一致 */
const ROUTE_ROLES: Record<string, Role[]> = {
  'dashboard': [Role.ADMIN, Role.OPS, Role.TECH],
  'channels': [Role.ADMIN, Role.OPS],
  'orders': [Role.ADMIN, Role.OPS, Role.TECH, Role.PARTNER, Role.DOWNSTREAM],
  'reports': [Role.ADMIN, Role.OPS, Role.TECH, Role.PARTNER, Role.DOWNSTREAM],
  'crypto-tool': [Role.TECH],
  'api-docs': [Role.ADMIN, Role.OPS, Role.TECH, Role.DOWNSTREAM],
  'profile': [Role.ADMIN, Role.OPS, Role.TECH, Role.PARTNER, Role.DOWNSTREAM],
  'tracking': [Role.ADMIN, Role.OPS, Role.TECH],
  'policies': [Role.ADMIN, Role.OPS, Role.TECH],
  'quarantine': [Role.ADMIN, Role.OPS, Role.TECH],
};

const App: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<IndexRedirect />} />
        <Route path="dashboard" element={
          <ProtectedRoute roles={ROUTE_ROLES['dashboard']}>
            <Suspense fallback={<PageLoading />}><DashboardPage /></Suspense>
          </ProtectedRoute>
        } />
        <Route path="channels" element={
          <ProtectedRoute roles={ROUTE_ROLES['channels']}>
            <Suspense fallback={<PageLoading />}><ChannelsPage /></Suspense>
          </ProtectedRoute>
        } />
        <Route path="orders" element={
          <ProtectedRoute roles={ROUTE_ROLES['orders']}>
            <Suspense fallback={<PageLoading />}><OrdersPage /></Suspense>
          </ProtectedRoute>
        } />
        <Route path="orders/:id" element={
          <ProtectedRoute roles={ROUTE_ROLES['orders']}>
            <Suspense fallback={<PageLoading />}><OrderDetailPage /></Suspense>
          </ProtectedRoute>
        } />
        <Route path="reports" element={
          <ProtectedRoute roles={ROUTE_ROLES['reports']}>
            <Suspense fallback={<PageLoading />}><ReportsPage /></Suspense>
          </ProtectedRoute>
        } />
        <Route path="crypto-tool" element={
          <ProtectedRoute roles={ROUTE_ROLES['crypto-tool']}>
            <Suspense fallback={<PageLoading />}><CryptoToolPage /></Suspense>
          </ProtectedRoute>
        } />
        <Route path="api-docs" element={
          <ProtectedRoute roles={ROUTE_ROLES['api-docs']}>
            <Suspense fallback={<PageLoading />}><ApiDocsPage /></Suspense>
          </ProtectedRoute>
        } />
        <Route path="profile" element={
          <ProtectedRoute roles={ROUTE_ROLES['profile']}>
            <Suspense fallback={<PageLoading />}><ProfilePage /></Suspense>
          </ProtectedRoute>
        } />
        <Route path="tracking" element={
          <ProtectedRoute roles={ROUTE_ROLES['tracking']}>
            <Suspense fallback={<PageLoading />}><TrackingPage /></Suspense>
          </ProtectedRoute>
        } />
        <Route path="policies" element={
          <ProtectedRoute roles={ROUTE_ROLES['policies']}>
            <Suspense fallback={<PageLoading />}><PoliciesPage /></Suspense>
          </ProtectedRoute>
        } />
        <Route path="quarantine" element={
          <ProtectedRoute roles={ROUTE_ROLES['quarantine']}>
            <Suspense fallback={<PageLoading />}><QuarantinePage /></Suspense>
          </ProtectedRoute>
        } />
      </Route>
    </Routes>
  );
};

export default App;
